import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PERSONAL } from "../data/portfolioData";
import HangingIcons from "../components/ui/HangingIcons";
import Reveal from "../components/ui/Reveal";
import Footer from "../components/Footer";

const Corner = ({ pos }) => (
  <span className={`pointer-events-none animate-pulse h-2 w-2 rounded-full bg-accent/50 absolute ${pos}`} />
);

const RotatingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % PERSONAL.words.length), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <span className="relative inline-block text-left text-ink min-w-[10ch]">
      <AnimatePresence mode="wait">
        <motion.span
          key={PERSONAL.words[i]}
          className="inline-block"
          initial={{ opacity: 0, y: 10, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -30, x: 30, filter: "blur(8px)", scale: 1.5 }}
          transition={{ duration: 0.4 }}
        >
          {PERSONAL.words[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

// Image that quietly removes itself (and tells the parent) if the file isn't there yet.
const OptionalImg = ({ src, alt = "", className = "", onStatus }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={className}
      onLoad={() => onStatus?.(true)}
      onError={() => {
        setFailed(true);
        onStatus?.(false);
      }}
    />
  );
};

const HobbyTile = ({ hobby }) => (
  <div className={`p-4 sm:p-8 relative overflow-hidden min-h-[280px] border-b lg:border-r border-ink/15 ${hobby.span}`}>
    {hobby.photo && (
      <>
        <OptionalImg src={hobby.photo} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-primary/10" />
      </>
    )}
    <div className="relative z-10">
      <p className="text-xl md:text-2xl text-ink">{hobby.title}</p>
      <p className="text-sm md:text-base text-ink/75 my-2 max-w-sm leading-relaxed">{hobby.text}</p>
    </div>
    <div aria-hidden="true" className="absolute -right-4 -bottom-8 text-[10rem] opacity-15 select-none">
      {hobby.emoji}
    </div>
  </div>
);

const AdventureTile = () => {
  const { title, text, icons } = PERSONAL.adventure;
  return (
    <div className="lg:col-span-6 grid grid-cols-1 md:grid-cols-5 gap-6 p-4 sm:p-8 border-b border-ink/15">
      <div className="md:col-span-2 flex flex-col justify-center">
        <p className="text-xl md:text-2xl text-ink">{title}</p>
        <p className="text-sm md:text-base text-ink/75 my-2 max-w-sm leading-relaxed">{text}</p>
      </div>
      <div className="md:col-span-3 flex items-center justify-center">
        <HangingIcons icons={icons} />
      </div>
    </div>
  );
};

// Polaroid-style strip. Hidden until at least one photo exists.
const Gallery = () => {
  const [loaded, setLoaded] = useState({});
  const anyLoaded = Object.values(loaded).some(Boolean);
  return (
    <div className={anyLoaded ? "py-10 lg:py-16" : "h-0 overflow-hidden"}>
      <div className="flex flex-wrap justify-center gap-6 lg:gap-10">
        {PERSONAL.gallery.map((photo, i) =>
          loaded[photo.src] === false ? null : (
            <motion.figure
              key={photo.src}
              className="bg-card p-3 pb-4 rounded-md shadow-[0_14px_36px_rgb(33_29_27/0.14)] border border-ink/10 w-56 lg:w-64"
              style={{ rotate: i % 2 ? 3 : -3 }}
              whileHover={{ rotate: 0, scale: 1.05, y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
            >
              <OptionalImg
                src={photo.src}
                alt={photo.caption}
                className="w-full aspect-[4/5] object-cover rounded-sm"
                onStatus={(ok) => setLoaded((l) => ({ ...l, [photo.src]: ok }))}
              />
              <figcaption className="mt-3 text-center text-sm text-ink/70">{photo.caption}</figcaption>
            </motion.figure>
          )
        )}
      </div>
    </div>
  );
};

const ThankYou = () => (
  <Reveal className="relative my-16 lg:my-24 rounded-3xl border border-ink/10 bg-card overflow-hidden">
    <div className="absolute inset-0 bg-grid opacity-60" />
    {/* soft glows along the bottom edge */}
    {[12, 45, 78].map((left, i) => (
      <motion.span
        key={left}
        className="absolute -bottom-10 h-24 w-40 rounded-full bg-accent/30 blur-2xl"
        style={{ left: `${left}%` }}
        animate={{ opacity: [0.4, 0.9, 0.4] }}
        transition={{ duration: 3 + i, repeat: Infinity, ease: "easeInOut" }}
      />
    ))}
    <div className="relative py-24 lg:py-36 px-6 text-center">
      <p className="text-3xl md:text-5xl lg:text-6xl font-semibold leading-tight">
        <span className="bg-gradient-to-r from-accent via-[#f07aa0] to-[#d4386b] bg-clip-text text-transparent">
          {PERSONAL.thanks.line1}
        </span>
        <br />
        <span className="text-ink">{PERSONAL.thanks.line2}</span>
        <span className="text-accent">.</span>
      </p>
    </div>
  </Reveal>
);

const Personal = ({ navigate }) => (
  <motion.section
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, transition: { delay: 1.2, duration: 0.4 } }}
    className="max-w-7xl mx-auto px-4 pt-28 xl:pt-36"
  >
    <div className="min-h-[30dvh] grid place-content-center">
      <h1 className="text-4xl lg:text-7xl font-semibold text-center mt-6 py-6 bg-clip-text text-transparent bg-gradient-to-b from-ink/50 via-ink to-ink leading-tight">
        Hello again! <br /> Outside of work I'm just{" "}
        <span className="relative inline-block bg-blush px-2 py-2 rounded-sm">
          <span className="text-ink">{PERSONAL.nickname}</span>
          <Corner pos="-right-[2px] -top-[2px]" />
          <Corner pos="-right-[2px] -bottom-[2px]" />
          <Corner pos="-left-[2px] -top-[2px]" />
          <Corner pos="-left-[2px] -bottom-[2px]" />
        </span>
      </h1>
    </div>

    <div className="min-h-[12rem] flex justify-center items-center px-4 py-8">
      <div className="text-2xl sm:text-3xl lg:text-4xl mx-auto text-ink/60 text-center">
        {PERSONAL.nickname} is <br className="lg:hidden" />
        <RotatingWord />
      </div>
    </div>

    <div className="relative z-20 py-10 lg:py-24">
      <h2 className="text-3xl lg:text-5xl max-w-5xl mx-auto text-center tracking-tight font-medium">
        {PERSONAL.hobbiesTitle}
      </h2>
      <p className="text-sm lg:text-base max-w-2xl my-4 mx-auto text-center text-ink/75">{PERSONAL.hobbiesIntro}</p>
      <div className="grid grid-cols-1 lg:grid-cols-6 mt-12 xl:border rounded-md border-ink/15 overflow-hidden">
        {PERSONAL.hobbies.map((h) => (
          <HobbyTile key={h.title} hobby={h} />
        ))}
        <AdventureTile />
      </div>
    </div>

    <Gallery />
    <ThankYou />
    <Footer navigate={navigate} showPersonalLink={false} />
  </motion.section>
);

export default Personal;
